import React, { useEffect, useRef, useState, memo } from 'react';
import { X, Pause, Play, StopCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * TerminalConsoleModal - Real-time streaming console output modal
 *
 * Features:
 * - Batched streaming display (updates every 100ms to prevent OOM)
 * - Auto-scroll with pause/resume
 * - Rich text rendering (emojis, markdown, etc.)
 * - User question display in header
 * - Responsive design
 * - Stop button for cancelling operations
 * - Optimized with React.memo to prevent unnecessary re-renders
 */
const TerminalConsoleModal = memo(({
  isOpen,
  onClose,
  consoleOutput,
  userQuestion,
  isStreaming,
  onStop
}) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const consoleRef = useRef(null);
  const lastScrollTop = useRef(0);

  // Reset auto-scroll to true when modal opens
  useEffect(() => {
    if (isOpen) {
      setAutoScroll(true);
    }
  }, [isOpen]);

  // ESC key handling - only when NOT streaming (safe for historical viewing)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !isStreaming) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isStreaming, onClose]);

  // Scroll to bottom when modal opens with content (for historical investigations)
  useEffect(() => {
    if (isOpen && consoleRef.current && consoleOutput) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (consoleRef.current) {
          consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
          lastScrollTop.current = consoleRef.current.scrollTop;
        }
      }, 0);
    }
  }, [isOpen]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (autoScroll && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
      lastScrollTop.current = consoleRef.current.scrollTop;
    }
  }, [consoleOutput, autoScroll]);

  // Detect user scroll
  const handleScroll = (e) => {
    const element = e.target;
    const isScrolledToBottom =
      Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;

    // If user scrolled up, pause auto-scroll
    if (element.scrollTop < lastScrollTop.current) {
      setAutoScroll(false);
    }
    // If user scrolled to bottom, resume auto-scroll
    else if (isScrolledToBottom) {
      setAutoScroll(true);
    }

    lastScrollTop.current = element.scrollTop;
  };

  const toggleAutoScroll = () => {
    setAutoScroll(!autoScroll);
    if (!autoScroll && consoleRef.current) {
      // Scroll to bottom when resuming
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-11/12 h-5/6 max-w-6xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <div className={`w-3 h-3 rounded-full ${isStreaming ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Investigation Console
              </h2>
            </div>
            {userQuestion && (
              <div className="text-sm text-gray-600 dark:text-gray-400 truncate min-w-0">
                {userQuestion}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Auto-scroll toggle */}
            <button
              onClick={toggleAutoScroll}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={autoScroll ? "Pause auto-scroll" : "Resume auto-scroll"}
            >
              {autoScroll ? (
                <Pause className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Play className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Stop button - only show when streaming */}
            {isStreaming && onStop && (
              <button
                onClick={onStop}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Stop analysis"
              >
                <StopCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Close console"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Console content */}
        <div
          ref={consoleRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900 font-mono text-sm"
        >
          <pre className="whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">
            {consoleOutput}
          </pre>

          {/* Streaming indicator */}
          {isStreaming && (
            <div className="mt-2 flex items-center space-x-2 text-green-600 dark:text-green-400">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs">Streaming...</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
            <div>
              {consoleOutput.length} characters
            </div>
            <div className="flex items-center space-x-2">
              {!autoScroll && (
                <span className="text-orange-600 dark:text-orange-400">
                  Auto-scroll paused
                </span>
              )}
              {isStreaming && (
                <span className="text-green-600 dark:text-green-400">
                  Live streaming active
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Display name for debugging
TerminalConsoleModal.displayName = 'TerminalConsoleModal';

export default TerminalConsoleModal;
